import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { inventoryApi, type InventoryItem } from '@/api/services';
import type { Location } from '@/api/services/locations.api';

interface BulkTransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: InventoryItem[];
  locations: Location[];
  onTransferred: (movedCount: number) => void;
}

/**
 * Move several items between locations in one shipment.
 *
 * Unlike a bulk field edit, this is genuinely all-or-nothing: the server writes
 * a single shipment covering every line, so there is no half-transferred state
 * to report. That makes it worth spending effort *before* submission instead —
 * availability at the chosen source is shown per item and quantities are capped
 * to it, so the request that gets sent is one the server can accept.
 */
export function BulkTransferDialog({
  open,
  onOpenChange,
  items,
  locations,
  onTransferred,
}: BulkTransferDialogProps) {
  const [sourceId, setSourceId] = useState('');
  const [destId, setDestId] = useState('');
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSourceId('');
      setDestId('');
      setQuantities({});
      setNotes('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  /** Locations that actually hold at least one of the selected items. */
  const sourceOptions = useMemo(() => {
    const holding = new Set<string>();
    for (const item of items) {
      for (const level of item.inventoryLevels ?? []) {
        if (level.availableQty > 0) holding.add(level.locationId);
      }
    }
    return locations.filter((l) => holding.has(l.id));
  }, [items, locations]);

  const availableAtSource = useCallback(
    (item: InventoryItem) =>
      item.inventoryLevels?.find((l) => l.locationId === sourceId)?.availableQty ?? 0,
    [sourceId]
  );

  const rows = useMemo(
    () =>
      items.map((item) => {
        const available = sourceId ? availableAtSource(item) : 0;
        const raw = quantities[item.id] ?? '';
        const qty = raw === '' ? 0 : Number(raw);
        return {
          item,
          available,
          qty,
          stocked: available > 0,
          overdrawn: qty > available,
        };
      }),
    [items, sourceId, quantities, availableAtSource]
  );

  const movable = rows.filter((r) => r.stocked);
  const chosen = rows.filter((r) => r.qty > 0);
  const anyOverdrawn = rows.some((r) => r.overdrawn);
  const canSubmit = !!sourceId && !!destId && chosen.length > 0 && !anyOverdrawn && !submitting;

  const fillAll = () => {
    const next: Record<string, string> = {};
    for (const row of rows) if (row.stocked) next[row.item.id] = String(row.available);
    setQuantities(next);
  };

  const handleTransfer = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await inventoryApi.transfer({
        sourceLocationId: sourceId,
        destinationLocationId: destId,
        items: chosen.map((r) => ({ itemId: r.item.id, quantity: r.qty })),
        notes: notes || undefined,
      });
      onTransferred(chosen.length);
      onOpenChange(false);
    } catch (e) {
      const message =
        (e as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message ??
        'The transfer could not be completed';
      setError(Array.isArray(message) ? message.join('. ') : message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Transfer {items.length} item{items.length > 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription>
            Moves stock as a single shipment. Items with no stock at the source cannot be included.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
          <div>
            <Label htmlFor="transfer-source">From</Label>
            <Select value={sourceId} onValueChange={setSourceId}>
              <SelectTrigger id="transfer-source" className="mt-1">
                <SelectValue placeholder="Source location" />
              </SelectTrigger>
              <SelectContent>
                {sourceOptions.length === 0 ? (
                  <div className="px-2 py-3 text-sm text-muted-foreground">
                    None of the selected items are in stock anywhere
                  </div>
                ) : (
                  sourceOptions.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <ArrowRight className="mb-3 h-4 w-4 text-muted-foreground" />

          <div>
            <Label htmlFor="transfer-dest">To</Label>
            <Select value={destId} onValueChange={setDestId}>
              <SelectTrigger id="transfer-dest" className="mt-1">
                <SelectValue placeholder="Destination location" />
              </SelectTrigger>
              <SelectContent>
                {locations
                  .filter((l) => l.id !== sourceId)
                  .map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {sourceId && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {movable.length} of {items.length} selected item
                {items.length > 1 ? 's are' : ' is'} stocked here
              </p>
              <Button variant="ghost" size="sm" onClick={fillAll} disabled={movable.length === 0}>
                Move everything available
              </Button>
            </div>

            <ScrollArea className="max-h-72 rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Item</th>
                    <th className="px-3 py-2 text-right font-medium">Available</th>
                    <th className="px-3 py-2 text-right font-medium">Move</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map(({ item, available, stocked, overdrawn }) => (
                    <tr key={item.id} className={stocked ? '' : 'opacity-50'}>
                      <td className="px-3 py-2">
                        <p className="font-medium">{item.sku}</p>
                        <p className="text-xs text-muted-foreground">{item.name}</p>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {stocked ? (
                          `${available} ${item.unit ?? ''}`
                        ) : (
                          <span className="text-xs text-muted-foreground">Not stocked here</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Input
                          type="number"
                          min={0}
                          max={available}
                          disabled={!stocked}
                          value={quantities[item.id] ?? ''}
                          onChange={(e) =>
                            setQuantities((prev) => ({ ...prev, [item.id]: e.target.value }))
                          }
                          placeholder="0"
                          className={`ml-auto w-24 text-right ${
                            overdrawn ? 'border-status-destructive-foreground' : ''
                          }`}
                        />
                        {overdrawn && (
                          <p className="mt-1 text-xs text-status-destructive-foreground">
                            Only {available} available
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>

            <div>
              <Label htmlFor="transfer-notes">Notes</Label>
              <Input
                id="transfer-notes"
                className="mt-1"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional. Recorded against the shipment"
              />
            </div>
          </>
        )}

        {error && (
          <p className="text-sm text-status-destructive-foreground" role="alert">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleTransfer} disabled={!canSubmit}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {chosen.length === 0
              ? 'Set a quantity'
              : `Transfer ${chosen.length} item${chosen.length > 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
